import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getSajuText, getSajuStructured, getBirthAnimal, lunarToSolar } from '../lib/sajuCalc';

const STORAGE_KEY = 'iching_saju_input';

function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSaju(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
`;

const Title = styled.h3`
  color: #a78bfa;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 16px 0;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SkipLabel = styled.label`
  color: #9ca3af;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  cursor: pointer;
  accent-color: #8b5cf6;
  width: 16px;
  height: 16px;
`;

const CalToggle = styled.div`
  display: flex;
  border: 1px solid #444;
  border-radius: 8px;
  overflow: hidden;
`;

const CalBtn = styled.button`
  background: ${p => p.$active ? '#8b5cf6' : 'transparent'};
  color: ${p => p.$active ? '#fff' : '#9ca3af'};
  border: none;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: ${p => p.$active ? '700' : '400'};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${p => p.$active ? '#8b5cf6' : '#2a2a2a'};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  background: #111;
  border: 1px solid #333;
  border-radius: 8px;
  color: #e5e5e5;
  font-size: 14px;
  padding: 8px 10px;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const LeapRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
`;

const LeapLabel = styled.label`
  color: #9ca3af;
  font-size: 12px;
  cursor: pointer;
  user-select: none;
`;

const Preview = styled.div`
  margin-top: 14px;
  padding: 10px 14px;
  background: #111;
  border-radius: 8px;
  color: #a78bfa;
  font-size: 13px;
  letter-spacing: 0.02em;
`;

const ConvertNote = styled.span`
  color: #6b7280;
  font-size: 11px;
  margin-left: 8px;
`;

const ErrorNote = styled.div`
  margin-top: 10px;
  color: #f87171;
  font-size: 12px;
`;

export default function SajuInput({ onSajuChange, onSajuStructuredChange }) {
  const saved = loadSaved();
  const [skip, setSkip] = useState(saved?.skip ?? false);
  const [calType, setCalType] = useState(saved?.calType ?? 'solar');
  const [isLeap, setIsLeap] = useState(saved?.isLeap ?? false);
  const [year, setYear] = useState(saved?.year ?? '');
  const [month, setMonth] = useState(saved?.month ?? '');
  const [day, setDay] = useState(saved?.day ?? '');
  const [hour, setHour] = useState(saved?.hour ?? '');
  const [convertError, setConvertError] = useState(false);

  useEffect(() => {
    if (saved && !saved.skip && saved.year && saved.month && saved.day) {
      const s = saved;
      compute(s.year, s.month, s.day, s.hour ?? '', s.calType ?? 'solar', s.isLeap ?? false, false);
    }
  }, []); // eslint-disable-line

  const compute = (y, m, d, h, type, leap, skipped = false) => {
    saveSaju({ skip: skipped, calType: type, isLeap: leap, year: y, month: m, day: d, hour: h });
    if (skipped) {
      onSajuChange('');
      onSajuStructuredChange?.(null);
      return;
    }
    const yi = Number(y), mi = Number(m), di = Number(d), hi = h !== '' ? Number(h) : null;
    if (!yi || !mi || !di) {
      onSajuChange('');
      onSajuStructuredChange?.(null);
      return;
    }

    if (type === 'lunar') {
      const solar = lunarToSolar(yi, mi, di, leap);
      if (!solar) {
        setConvertError(true);
        onSajuChange('');
        onSajuStructuredChange?.(null);
        return;
      }
      setConvertError(false);
      onSajuChange(getSajuText(solar.year, solar.month, solar.day, hi));
      onSajuStructuredChange?.(getSajuStructured(solar.year, solar.month, solar.day, hi));
    } else {
      setConvertError(false);
      onSajuChange(getSajuText(yi, mi, di, hi));
      onSajuStructuredChange?.(getSajuStructured(yi, mi, di, hi));
    }
  };

  const handleSkip = (e) => {
    const checked = e.target.checked;
    setSkip(checked);
    compute(year, month, day, hour, calType, isLeap, checked);
  };

  const handleCalType = (type) => {
    setCalType(type);
    setIsLeap(false);
    setConvertError(false);
    compute(year, month, day, hour, type, false, skip);
  };

  // solar date used for preview + saju text
  const getSolarForPreview = () => {
    const yi = Number(year), mi = Number(month), di = Number(day);
    if (!yi || !mi || !di) return null;
    if (calType === 'lunar') {
      return lunarToSolar(yi, mi, di, isLeap);
    }
    return { year: yi, month: mi, day: di };
  };

  const solar = !skip ? getSolarForPreview() : null;
  const sajuPreview = solar
    ? getSajuText(solar.year, solar.month, solar.day, hour !== '' ? Number(hour) : null)
    : null;
  const animal = !skip && year ? getBirthAnimal(
    calType === 'lunar' && solar ? solar.year : Number(year)
  ) : null;

  return (
    <Card>
      <Title>Step 1 — 사주 입력 (선택)</Title>
      <TopRow>
        <SkipRow>
          <Checkbox
            type="checkbox"
            id="skip-saju"
            checked={skip}
            onChange={handleSkip}
          />
          <SkipLabel htmlFor="skip-saju">사주 없이 점보기</SkipLabel>
        </SkipRow>

        {!skip && (
          <CalToggle>
            <CalBtn $active={calType === 'solar'} onClick={() => handleCalType('solar')}>
              양력
            </CalBtn>
            <CalBtn $active={calType === 'lunar'} onClick={() => handleCalType('lunar')}>
              음력
            </CalBtn>
          </CalToggle>
        )}
      </TopRow>

      {!skip && (
        <>
          <Grid>
            <FieldGroup>
              <Label>출생연도</Label>
              <Input
                type="number" min="1900" max="2024"
                placeholder="예: 1990"
                value={year}
                onChange={e => { setYear(e.target.value); compute(e.target.value, month, day, hour, calType, isLeap, skip); }}
              />
            </FieldGroup>
            <FieldGroup>
              <Label>월</Label>
              <Input
                type="number" min="1" max="12"
                placeholder="1~12"
                value={month}
                onChange={e => { setMonth(e.target.value); compute(year, e.target.value, day, hour, calType, isLeap, skip); }}
              />
            </FieldGroup>
            <FieldGroup>
              <Label>일</Label>
              <Input
                type="number" min="1" max="30"
                placeholder="1~30"
                value={day}
                onChange={e => { setDay(e.target.value); compute(year, month, e.target.value, hour, calType, isLeap, skip); }}
              />
            </FieldGroup>
            <FieldGroup>
              <Label>출생시 (선택)</Label>
              <Input
                type="number" min="0" max="23"
                placeholder="0~23"
                value={hour}
                onChange={e => { setHour(e.target.value); compute(year, month, day, e.target.value, calType, isLeap, skip); }}
              />
            </FieldGroup>
          </Grid>

          {calType === 'lunar' && (
            <LeapRow>
              <Checkbox
                type="checkbox"
                id="leap-month"
                checked={isLeap}
                onChange={e => { setIsLeap(e.target.checked); compute(year, month, day, hour, calType, e.target.checked, skip); }}
              />
              <LeapLabel htmlFor="leap-month">윤달</LeapLabel>
            </LeapRow>
          )}

          {convertError && (
            <ErrorNote>음력 날짜 변환에 실패했습니다. 날짜를 다시 확인해 주세요.</ErrorNote>
          )}

          {sajuPreview && !convertError && (
            <Preview>
              {sajuPreview}{animal ? ` · 띠: ${animal}` : ''}
              {calType === 'lunar' && solar && (
                <ConvertNote>
                  (양력 {solar.year}.{solar.month}.{solar.day} 기준)
                </ConvertNote>
              )}
            </Preview>
          )}
        </>
      )}
    </Card>
  );
}
